import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, Form, Upload, Button, message, Alert, Progress } from 'antd';
import { 
  InboxOutlined, 
  ArrowLeftOutlined, 
  UploadOutlined, 
  WarningOutlined
} from '@ant-design/icons';

// 导入产品图片相关 API
import { uploadImage } from '@/api/productImages';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import type { RcFile, UploadFile, UploadChangeParam } from 'antd/es/upload/interface';

// 上传进度状态
interface UploadProgress {
  filename: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

export const ProductImageUpload = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productId = searchParams.get('productId');
  
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgressList, setUploadProgressList] = useState<UploadProgress[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);

  // 校验 productId
  useEffect(() => {
    if (!productId) {
      message.error('无效的产品 ID');
      navigate('/products');
    }
  }, [productId, navigate]);

  if (!productId) {
    return <LoadingSpinner />;
  }

  // 文件上传前验证
  const beforeUpload = (file: RcFile) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      message.error(`${file.name} 大小超过 5MB，已跳过`);
      return Upload.LIST_IGNORE;
    }

    const fileName = file.name.toLowerCase();
    const validExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
    const isValidType = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidType) {
      message.error(`${file.name} 格式不支持，已跳过`);
      return Upload.LIST_IGNORE;
    }

    // 检查是否已经有同名文件
    const hasDuplicate = selectedFiles.some(f => f.name === file.name);
    if (hasDuplicate) {
      message.warning(`${file.name} 已存在列表中，已跳过`);
      return Upload.LIST_IGNORE;
    }

    return false; // 手动控制上传
  };

  // 处理文件列表变化
  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    const newFileList = info.fileList.slice(-100); // 最多支持 100 张图片
    setFileList(newFileList);
    
    // 更新选中的文件列表
    const files = newFileList
      .filter(f => f.originFileObj)
      .map(f => f.originFileObj as File);
    setSelectedFiles(files);
    
    // 重置上传完成状态
    setUploadComplete(false);
  };

  // 批量提交上传
  const onFinish = async () => {
    if (selectedFiles.length === 0) {
      message.error('请选择要上传的图片');
      return;
    }

    setSubmitting(true);
    setError(null);
    setUploadComplete(false);
    
    // 初始化进度列表
    const progressList: UploadProgress[] = selectedFiles.map(f => ({
      filename: f.name,
      status: 'pending',
      progress: 0,
    }));
    setUploadProgressList(progressList);

    let successCount = 0;
    let errorCount = 0;
    const results: { filename: string; success: boolean; error?: string }[] = [];

    // 逐个上传
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      
      // 更新当前文件状态为上传中
      setUploadProgressList(prev => 
        prev.map((p, idx) => idx === i ? { ...p, status: 'uploading', progress: 50 } : p)
      );

      try {
        await uploadImage(productId, file);
        
        // 更新为成功状态
        setUploadProgressList(prev => 
          prev.map((p, idx) => idx === i ? { ...p, status: 'success', progress: 100 } : p)
        );
        successCount++;
        results.push({ filename: file.name, success: true });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '上传失败';
        
        // 更新为失败状态
        setUploadProgressList(prev => 
          prev.map((p, idx) => idx === i ? { ...p, status: 'error', progress: 0, error: errorMessage } : p)
        );
        errorCount++;
        results.push({ filename: file.name, success: false, error: errorMessage });
      }
    }

    // 上传完成
    setUploadComplete(true);
    setSubmitting(false);

    // 显示结果
    if (successCount > 0 && errorCount === 0) {
      message.success(`全部上传成功！共 ${successCount} 张图片`);
    } else if (successCount > 0 && errorCount > 0) {
      message.warning(`${successCount} 张成功，${errorCount} 张失败`);
    } else {
      message.error('全部上传失败，请重试');
      return;
    }

    // 刷新产品图片列表缓存
    queryClient.invalidateQueries({ queryKey: ['product-images', productId] });

    // 延迟 1 秒后返回列表页
    setTimeout(() => {
      navigate(`/products/${productId}/images`);
    }, 1000);
  };

  // 返回图片列表
  const handleBack = () => {
    navigate(`/products/${productId}/images`);
  };

  return (
    <div className="w-full space-y-8">
      {/* 标题+返回区 */}
      <div className="flex flex-wrap justify-between items-center gap-4 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <UploadOutlined className="text-blue-500" />
            上传产品图片
          </h1>
          <p className="text-gray-500 mt-1">
            为产品 ID: <span className="font-mono text-blue-600">{productId}</span> 上传资源图片
          </p>
        </div>
        <Button 
          onClick={handleBack}
          icon={<ArrowLeftOutlined />}
          className="hover:bg-gray-50 transition-colors"
        >
          返回图片列表
        </Button>
      </div>
      
      {/* 错误提示 */}
      {error && (
        <ErrorAlert 
          message="上传失败" 
          description={error} 
          className="mb-0 max-w-2xl"
          showIcon
        />
      )}

      {/* 上传表单卡片 */}
      <Card 
        title="上传新图片资源"
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 max-w-2xl"
        style={{ 
          padding: '24px',
          '--ant-card-head-font-size': '18px',
          '--ant-card-head-font-weight': 600,
          '--ant-card-head-color': '#1D2129'
        } as React.CSSProperties}
      >
        <Alert
          message="上传规则说明"
          description={
            <div className="space-y-1 text-gray-700">
              <p>• 支持格式：PNG、JPG、JPEG、WebP、GIF（推荐使用 WebP，体积更小。）</p>
              <p>• 大小限制：单张图片最大 <strong className="text-red-600">5MB</strong></p>
              <p>• <strong className="text-orange-600">同名覆盖规则</strong>：上传与已有文件同名的图片，会自动覆盖旧版本并将版本号+1。</p>
              <p>• 覆盖后旧版本图片将被<strong className="text-red-600">永久删除</strong>，无法恢复。</p>
              <p>• 上传成功后系统会自动刷新图片列表，展示最新版本。</p>
            </div>
          }
          type="info"
          showIcon
          className="mb-8 rounded-lg"
        />

        <br />

        <Form 
          layout="vertical" 
          onFinish={onFinish} 
          className="space-y-8"
          requiredMark={false}
        >
          <Form.Item
            label="选择图片文件（支持批量上传，最多 100 张）"
            rules={[{ required: true, message: '请选择要上传的图片文件' }]}
            validateStatus={selectedFiles.length > 0 ? 'success' : (submitting ? 'validating' : 'error')}
            help={selectedFiles.length > 0
              ? <span className="text-green-500 flex items-center mt-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  已选择 {selectedFiles.length} 张图片
                </span>
              : <span className="text-red-500 flex items-center mt-1">
                  <WarningOutlined className="mr-1" />
                  请选择 PNG/JPG/JPEG/WebP/GIF 格式的图片（单张最大 5MB，最多 100 张）
                </span>
            }
            style={{ marginBottom: 0 }}
          >
            <Upload.Dragger
              accept=".png,.jpg,.jpeg,.webp,.gif"
              multiple
              maxCount={100}
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleChange}
              onRemove={() => {
                // 移除文件时更新列表
                setSelectedFiles(prev => {
                  const newFiles = [...prev];
                  return newFiles;
                });
              }}
              disabled={submitting}
              showUploadList={true}
              className="rounded-lg"
              style={{ 
                border: selectedFiles.length > 0 ? '2px solid #52c41a' : '2px dashed #d9d9d9',
                background: selectedFiles.length > 0 ? '#f0f9eb' : 'transparent',
                padding: '24px',
                transition: 'all 0.3s'
              } as React.CSSProperties}
            >
              {fileList.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-5xl text-blue-500 mb-4">
                    <InboxOutlined />
                  </div>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    点击或拖拽图片到此区域上传
                  </p>
                  <p className="text-gray-500 text-sm">
                    支持 PNG、JPG、JPEG、WebP、GIF 格式 · 单张最大 5MB · 最多 100 张
                  </p>
                </div>
              ) : null}
            </Upload.Dragger>

            {/* 上传进度列表 */}
            {uploadProgressList.length > 0 && (
              <div className="mt-6 space-y-3">
                <h4 className="text-sm font-medium text-gray-700">上传进度</h4>
                {uploadProgressList.map((progress, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-800 truncate max-w-xs">{progress.filename}</span>
                      <span className={`text-xs font-medium ${
                        progress.status === 'success' ? 'text-green-600' :
                        progress.status === 'error' ? 'text-red-600' :
                        progress.status === 'uploading' ? 'text-blue-600' :
                        'text-gray-500'
                      }`}>
                        {progress.status === 'pending' && '等待中'}
                        {progress.status === 'uploading' && '上传中...'}
                        {progress.status === 'success' && '✓ 成功'}
                        {progress.status === 'error' && '✗ 失败'}
                      </span>
                    </div>
                    <Progress 
                      percent={progress.progress} 
                      status={progress.status === 'error' ? 'exception' : progress.status === 'success' ? 'success' : 'active'}
                      showInfo={false}
                      size="small"
                    />
                    {progress.error && (
                      <p className="text-xs text-red-500 mt-1">{progress.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Form.Item>

          <br />

          <Form.Item className="mb-0">
            <div className="flex space-x-4">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                disabled={submitting || (!uploadComplete && selectedFiles.length === 0)}
                className="flex-1 hover:shadow-md transition-shadow"
                style={{ borderRadius: '8px', fontSize: '16px' } as React.CSSProperties}
              >
                {submitting ? '上传中...' : (uploadComplete ? '上传完成' : `确认上传 (${selectedFiles.length} 张)`)}
              </Button>
              <Button 
                size="large" 
                onClick={handleBack} 
                disabled={submitting}
                style={{ borderRadius: '8px' } as React.CSSProperties}
              >
                取消
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};